import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill/dist/quill.snow.css';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { Box, Grid, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/hooks';
import {
  getPresignedUrlRequest,
  addTextNoteRequest,
  editSingleNoteRequest,
  NotesSavingStatus,
} from '../../redux/note/noteSlice';
import './TextEditor.css';
import { RoundedSkeleton } from '../ClientTable/LoadingSkeleton';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

Quill.register('modules/imageResize', ImageResize);

const EditorContainer = styled(Grid)(() => ({
  borderRadius: '2px',
  maxWidth: '100%',
  color: '#000',
  position: 'relative',
  lineHeight: '20px',
  fontWeight: 400,
  textAlign: 'left',
  height: '100%',
  background: '#fff',
}));

function RichTextEditor() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const quillRef = useRef(null);
  const clientId = useParams<{ id: string }>().id;
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const noteId = useAppSelector((state) => state.note.selectedNoteId);
  const notes = useAppSelector((state) => state.note.notes);
  const initialHtml = useAppSelector((state) => state.note.initialHtml);
  const forceHtmlChange = useAppSelector((state) => state.note.forceHtmlChange);
  const key = useAppSelector((state) => state.note.key);
  const noteSavingStatus = useAppSelector((state) => state.note.savingStatus);
  const getPresignedUrlLoading = useAppSelector((state) => state.note.getPresignedUrlLoading);
  const suggestion = useAppSelector((state) => state.note.suggestion);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const noteSaveHandler = (note: string, html: string, tempId: string) => {
    if (!clientId || !coachId) return;
    dispatch(
      addTextNoteRequest({
        tempId,
        clientId,
        coachId,
        text: note,
        html,
      })
    );
  };

  const updateNoteHandler = (
    isForced: boolean,
    editorState: string,
    editorStateHtml: string,
    existingNoteId: string
  ) => {
    if (!clientId || !coachId) return;
    const handledNote = editorState || ' ';
    dispatch(
      editSingleNoteRequest({
        clientId,
        coachId,
        noteId: existingNoteId,
        text: handledNote,
        isForceSuggestions: isForced,
        html: editorStateHtml,
      })
    );
  };

  const createOrUpdatenote = (isForced: boolean, editorState: string, editorStateHtml: string) => {
    if (!checkPermissions(UserPermissionType.NOTE_MODULE_CREATE_AND_EDIT_NOTES)) {
      return;
    }
    if (!clientId || !coachId) return;
    // Check if there are any notes for the given date
    const noteData = notes?.find((note) => note.id === noteId);
    if (!noteData || !noteData.id) {
      // If there are no existing notes for the date, add a new note
      noteSaveHandler(editorState, editorStateHtml, noteId || '');
    } else {
      // If there is an existing note for the date, edit that note
      updateNoteHandler(isForced, editorState, editorStateHtml, noteData.id);
    }
  };

  const debouncedSaveDraftNotes = useCallback(
    debounce((editorState: string, editorStateHtml: string) => {
      createOrUpdatenote(false, editorState, editorStateHtml);
    }, 1000),
    [clientId, coachId, noteId]
  );

  const handleChange = (event: string) => {
    if (!checkPermissions(UserPermissionType.NOTE_MODULE_CREATE_AND_EDIT_NOTES)) {
      return;
    }
    setValue(event);
    // check if the note is empty or has only default html tags
    if (!event || event === '<p><br></p>') return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(event, 'text/html');
    const text = doc.body.textContent || '';
    debouncedSaveDraftNotes(text, event);
  };

  const ImageUpload = async (file: File) => {
    dispatch(
      getPresignedUrlRequest({
        clientId: clientId as string,
        contentType: 'image/jpeg',
        fileName: file?.name,
        fileObject: file,
      })
    );
  };

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];
        if (file) {
          ImageUpload(file); // upload data into s3
        }
      }
    };
  };

  useEffect(
    () => () => {
      debouncedSaveDraftNotes.cancel();
    },
    [debouncedSaveDraftNotes]
  );

  useEffect(() => {
    if (key) {
      const image = `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${key}`;
      const valueModified = `${value}<img src="${image}" />`;
      setValue(valueModified);
    }
  }, [key]);

  // Set initial value
  // use forceHtmlChange to update the value of the editor if same value is passed again
  useEffect(() => {
    setValue(initialHtml as string);
  }, [initialHtml, forceHtmlChange]);

  useEffect(() => {
    if (suggestion) {
      let editorStateHtml: string;
      if (value === '<p><br></p>') {
        editorStateHtml = `<p>${suggestion}</p>`;
      } else {
        editorStateHtml = `${value}<p>${suggestion}</p>`;
      }
      handleChange(editorStateHtml);
    }
  }, [suggestion]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          ['bold', 'italic', 'underline', 'strike'], // toggled buttons
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          [
            checkPermissions(UserPermissionType.NOTE_MODULE_CREATE_AND_EDIT_NOTES) && 'image',
            'link',
          ],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
      },
    }),
    []
  );

  if (getPresignedUrlLoading) {
    return <RoundedSkeleton variant="rounded" width="100%" height="100%" />;
  }

  return (
    <Box
      bgcolor="#fff"
      borderRadius="0 10px 10px 10px"
      border="1px solid rgba(124, 174, 190, 0.50)"
      height="100%"
      sx={{
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        scrollBehavior: 'smooth',
        height: '100%',
        // Hide scrollbar by default
        '&::-webkit-scrollbar': {
          width: '6px',
          backgroundColor: 'transparent',
        },
        // Show scrollbar on hover
        '&:hover::-webkit-scrollbar': {
          backgroundColor: (theme) => theme.palette.primary.light,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: (theme) => theme.palette.secondary.main,
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: (theme) => theme.palette.secondary.main,
        },
        // Styles for Firefox
        // Hide scrollbar by default
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        // Show scrollbar on hover
        '&:hover': {
          scrollbarColor: (theme) =>
            `${theme.palette.secondary.main} ${theme.palette.secondary.light}`,
        },
      }}
    >
      <EditorContainer>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          style={{
            height: '87%',
          }}
          className="no-border"
          placeholder={t('activity.createActivity.placeholder')}
          readOnly={!checkPermissions(UserPermissionType.NOTE_MODULE_CREATE_AND_EDIT_NOTES)}
        />
        {noteSavingStatus === NotesSavingStatus.SAVING && (
          <Box
            component="img"
            src="/assets/images/save-note.png"
            position="absolute"
            bottom={5}
            right={5}
          />
        )}
      </EditorContainer>
    </Box>
  );
}
export default RichTextEditor;
